/* eslint-disable max-len */
import {
  CentralCountrySettings,
  IamProviderType,
  SellerMessage,
} from '@1po/1po-bff-fe-spec/generated/backoffice/central_settings/model/CentralCountrySettings';
import { AddSellerRequest } from '@1po/1po-bff-fe-spec/generated/backoffice/central_settings/request/AddSellerRequest';
import { CentralSettingsUpdateRequest } from '@1po/1po-bff-fe-spec/generated/backoffice/central_settings/request/CentralSettingsUpdateRequest';
import { DeleteSellerRequest } from '@1po/1po-bff-fe-spec/generated/backoffice/central_settings/request/DeleteSellerRequest';
import { UpdateSellerDataProvidersRequest } from '@1po/1po-bff-fe-spec/generated/backoffice/central_settings/request/UpdateSellerDataProvidersRequest';
import { CentralSettingsResponse } from '@1po/1po-bff-fe-spec/generated/backoffice/central_settings/response/CentralSettingsResponse';
import {
  ADD_SELLER_ERROR_RESPONSE,
  ADD_SELLER_RESPONSE,
  DELETE_SELLER_ERROR_RESPONSE,
  DELETE_SELLER_RESPONSE,
  UPDATE_SELLER_DATA_PROVIDERS_ERROR_RESPONSE,
  UPDATE_SELLER_DATA_PROVIDERS_RESPONSE,
} from '@1po/1po-bff-fe-spec/generated/common/ResponseType';
import { createAction, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { createSelector } from 'reselect';
import { RootState } from 'app/AppStore';
import { FOUND, LOADING, SEARCH_STATUS } from 'utils';
import {
  CENTRAL_SETTINGS_NAMESPACE,
  DEFAULT_COUNTRY,
  DEFAULT_IAM_PROVIDER,
  GET_CENTRAL_SETTINGS_REQUEST,
  UPDATE_CENTRAL_SETTINGS_REQUEST,
  ADD_SELLER_REQUEST,
  UPDATE_SELLER_DATA_PROVIDERS_REQUEST,
  DELETE_SELLER_REQUEST,
} from './CentralSettings.types';

export type SellerErrorCode =
  | 'SELLER_ALREADY_EXISTS'
  | 'SELLER_NOT_FOUND'
  | 'INVALID_SELLER_NAME'
  | 'INVALID_SELLER_DATA_PROVIDERS';

export interface SellerCommandResponse {
  result: string;
  body?: SellerErrorCode;
}

export type SellerCommandStatus = 'IDLE' | 'SUCCESS' | 'ERROR';

export interface CentralSettingsState {
  country: string;
  countrySettings: CentralCountrySettings[];
  dirty: boolean;
  status: SEARCH_STATUS;
  sellerCommandStatus: SellerCommandStatus;
}

const initialState: CentralSettingsState = {
  country: DEFAULT_COUNTRY,
  countrySettings: [],
  dirty: false,
  status: LOADING,
  sellerCommandStatus: 'IDLE',
};

// Saga actions
export const getCentralSettingsRequestSaga = createAction(GET_CENTRAL_SETTINGS_REQUEST);
export const updateCentralSettingsRequestSaga = createAction<CentralSettingsUpdateRequest>(
  UPDATE_CENTRAL_SETTINGS_REQUEST,
);
export const addSellerRequested = createAction<AddSellerRequest>(ADD_SELLER_REQUEST);
export const updateSellerDataProvidersRequested = createAction<UpdateSellerDataProvidersRequest>(
  UPDATE_SELLER_DATA_PROVIDERS_REQUEST,
);
export const deleteSellerRequested = createAction<DeleteSellerRequest>(DELETE_SELLER_REQUEST);

export const addSellerResponse = createAction<SellerCommandResponse>(ADD_SELLER_RESPONSE);
export const addSellerErrorResponse = createAction<SellerCommandResponse>(ADD_SELLER_ERROR_RESPONSE);
export const updateSellerDataProvidersResponse = createAction<SellerCommandResponse>(
  UPDATE_SELLER_DATA_PROVIDERS_RESPONSE,
);
export const updateSellerDataProvidersErrorResponse = createAction<SellerCommandResponse>(
  UPDATE_SELLER_DATA_PROVIDERS_ERROR_RESPONSE,
);
export const deleteSellerResponse = createAction<SellerCommandResponse>(DELETE_SELLER_RESPONSE);
export const deleteSellerErrorResponse = createAction<SellerCommandResponse>(DELETE_SELLER_ERROR_RESPONSE);

// Slice
const slice = createSlice({
  name: CENTRAL_SETTINGS_NAMESPACE,
  initialState,
  reducers: {
    setCentralSettings: (state, action: PayloadAction<CentralSettingsResponse>) => {
      state.countrySettings = action.payload.countrySettings;
      state.dirty = false;
      state.status = FOUND;
    },
    setCountry: (state, action: PayloadAction<string>) => {
      state.country = action.payload;
    },
    setIamProvider: (state, action: PayloadAction<IamProviderType>) => {
      const index = state.countrySettings.findIndex((setting) => setting.country === state.country);
      if (index === -1) {
        state.countrySettings = [
          ...state.countrySettings,
          { country: state.country, iamProvider: action.payload, allowMktpDeliveryOptions: false, sellers: [] },
        ];
        state.dirty = true;
      } else {
        if (state.countrySettings[index].iamProvider !== action.payload) {
          state.dirty = true;
        }
        state.countrySettings[index].iamProvider = action.payload;
      }
    },
    setMktpDeliveryOptions: (state, action: PayloadAction<boolean>) => {
      const index = state.countrySettings.findIndex((setting) => setting.country === state.country);
      if (index === -1) {
        state.countrySettings = [
          ...state.countrySettings,
          {
            country: state.country,
            iamProvider: DEFAULT_IAM_PROVIDER,
            allowMktpDeliveryOptions: action.payload,
            sellers: [],
          },
        ];
        state.dirty = true;
      } else {
        if (state.countrySettings[index].allowMktpDeliveryOptions !== action.payload) {
          state.dirty = true;
        }
        state.countrySettings[index].allowMktpDeliveryOptions = action.payload;
      }
    },
    setCentralSettingsStatus: (state, action: PayloadAction<SEARCH_STATUS>) => {
      state.status = action.payload;
    },
    resetDirty: (state) => {
      state.dirty = false;
    },
    setSellerCommandStatus: (state, action: PayloadAction<SellerCommandStatus>) => {
      state.sellerCommandStatus = action.payload;
    },
  },
});

export const {
  setCentralSettings,
  setCountry,
  setIamProvider,
  setMktpDeliveryOptions,
  setCentralSettingsStatus,
  resetDirty,
  setSellerCommandStatus,
} = slice.actions;

export const getCountrySettings = createSelector(
  (state: RootState) => state.centralSettings.countrySettings,
  (countrySettings: CentralCountrySettings[]) => countrySettings,
);

export const getCentralSettingsStatus = createSelector(
  (state: RootState) => state.centralSettings.status,
  (status: SEARCH_STATUS) => status,
);

export const getCountry = createSelector(
  (state: RootState) => state.centralSettings.country,
  (country: string) => country,
);

export const getIamProvider = createSelector(
  (state: RootState) => state.centralSettings.country,
  (state: RootState) => state.centralSettings.countrySettings,
  (country, countrySettings): IamProviderType =>
    countrySettings.find((settings) => settings.country === country)?.iamProvider ?? DEFAULT_IAM_PROVIDER,
);

export const getMktpDeliveryOptions = createSelector(
  (state: RootState) => state.centralSettings.country,
  (state: RootState) => state.centralSettings.countrySettings,
  (country, countrySettings): boolean =>
    countrySettings.find((settings) => settings.country === country)?.allowMktpDeliveryOptions ?? false,
);

export const selectSellersByCountry = createSelector(
  [
    (state: RootState) => state.centralSettings.countrySettings,
    (_state: RootState, countryCode: string) => countryCode,
  ],
  (countrySettings: CentralCountrySettings[], countryCode: string): SellerMessage[] =>
    countrySettings.find((settings) => settings.country === countryCode)?.sellers ?? [],
);

export const isDirty = createSelector(
  (state: RootState) => state.centralSettings.dirty,
  (dirty): boolean => dirty,
);

export const getSellerCommandStatus = createSelector(
  (state: RootState) => state.centralSettings.sellerCommandStatus,
  (sellerCommandStatus: SellerCommandStatus) => sellerCommandStatus,
);

export default slice.reducer;
